import React, { useEffect } from "react";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import "./LoyaltyCard.css"; // Import the CSS file

gsap.registerPlugin(MotionPathPlugin);

const LoyaltyCard = ({
  card_number,
  full_name,
  purchase_date,
  expiry_date,
}) => {
  useEffect(() => {
    gsap.set(".loyalty-card-container svg", { opacity: 1 });
    gsap.set(".scratches", { rotation: 70, x: 450, y: -10 });
    gsap.set("#tri2", { scale: 0.5 });

    // Wave animations
    gsap.fromTo(
      "#wave1",
      { x: 0, y: 0 },
      { duration: 5, x: -701, y: 815, repeat: -1, ease: "none" },
    );
    gsap.fromTo(
      "#wave2",
      { x: 0, y: 0 },
      {
        duration: 6,
        x: 804,
        y: -917,
        repeat: -1,
        ease: "none",
        onRepeat: () => starShine.play(0),
      },
    );

    // Circle rotation animations
    gsap.to("#tri1", {
      motionPath: {
        path: "#midC",
        align: "#midC",
        alignOrigin: [0.5, 0.5],
        autoRotate: true,
        start: 1,
        end: 0,
      },
      duration: 6,
      repeat: -1,
      ease: "none",
      repeatDelay: 1,
    });
    gsap.to("#tri2", {
      motionPath: {
        path: "#innerC",
        align: "#innerC",
        alignOrigin: [0.5, 0.5],
        autoRotate: true,
        start: 0,
        end: 1,
      },
      duration: 5,
      repeat: -1,
      ease: "none",
      repeatDelay: 1,
    });

    // Star animation
    const starShine = gsap
      .timeline()
      .set("#star", { scale: 0, transformOrigin: "50% 50%", x: 2, y: 10 })
      .to(
        "#star",
        {
          scale: 1,
          repeat: 1,
          yoyo: true,
          yoyoEase: true,
          duration: 0.4,
          ease: "power4",
        },
        0,
      )
      .fromTo(
        "#star",
        { rotate: -20 },
        { rotate: 120, duration: 0.8, ease: "none" },
        0,
      );
  }, []);

  // Display fallback values if user is not logged in
  const displayName = full_name || "Username";
  const displayExpiry = expiry_date || "2/30";

  // Format card number with spaces every 4 digits
  const formatCardNumber = (cardNumber) => {
    if (!cardNumber) return "**** **** **** ****";
    return cardNumber.toString().replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  return (
    <div className="loyalty-card-container">
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 600 368"
      >
        <defs>
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "#CF344A", stopOpacity: 1 }}
            />
            <stop
              offset="60%"
              style={{ stopColor: "#971132", stopOpacity: 1 }}
            />
          </linearGradient>

          <mask id="chipMask">
            <rect width="100%" height="100%" fill="#fff" />
            <path
              fill="none"
              stroke="#000"
              strokeWidth="3"
              d="M98,133c0,0,4.3,0,7.2,0c2.8,0,3.8,2,3.8,4s-1,6-4,6H89 M109,176.2c0,0,3.5-2.7,3.5-8.7c0-4-4-10-9-10 s-15,0-15,0 M103.5,143.5V156 M136.8,133c0,0-7.3-0.5-7.1,5c0.1,2.6,1.8,5.5,6.3,5.5c5.3-0.1,14.9-0.5,14.9-0.5 M150.4,157.5 c0,0-10.1,0-15.1,0s-9.1,6-9.1,10c0,6,3.5,8.7,3.5,8.7 M135.3,143.5V156"
            />
          </mask>

          <mask id="cardMask">
            <rect rx="20" ry="20" fill="#fff" width="600" height="368" />
          </mask>



          <clipPath id="orbClip1">
            <use href="#orb1" />
            <use href="#orb2" />
          </clipPath>
        </defs>

        <g mask="url(#cardMask)">
          <rect
            className="bg"
            fill="url(#bgGradient)"
            width="100%"
            height="100%"
          />

          <g
            fill="none"
            stroke="#FFEB3B"
            strokeWidth="2"
            strokeDasharray="6 2 4 2.5 4 3 3.5 3"
          >
            <path
              className="coil"
              d="M306.1,671.4C62.6,602.5-79.4,350.4-10.9,108.3s295.2-372.5,538.7-303.6"
            />
            <path
              id="midC"
              className="coil"
              d="M352.7,333.4c-79.2-30.9-137.8-142.9-100.6-235.7S386.5-16.5,449.6-11.7"
            />
            <path
              id="innerC"
              className="coil"
              d="M404.4,221c-39.6-7.4-65.7-45.6-58.3-85.2s41.6-63.7,81.2-56.3"
            />
          </g>

          <polygon id="tri1" fill="#FD3B84" points="0,47 0,0 44,22" />
          <polygon id="tri2" fill="#FFA726" points="0,47 0,0 44,22" />

          <g fill="#FF9800">
            <circle id="orb1" cx="123" cy="410" r="105" />
            <circle id="orb2" cx="540" cy="45" r="134" />
          </g>

          <g fill="#F44336">
            <path
              id="wave2"
              d="M-941,1510c89-58,147-115,211-201s142-153,210-195s144-42,208-86s69-130,70-173s5-129,38-193 c0,0,17.5-27.5,30.9-40.1C-159.6,609.4-137,593-137,593c89-58,147-115,211-201s142-153,210-195s144-42,208-86s69-130,70-173 s5-129,38-193v1765H-941z"
            />
          </g>
          <g fill="#16A24A">
            <path
              id="wave1"
              d="M1551.5-1044.5c0,0-63,27-131,91s-122,185-186,244s-163,152.7-206,263.3s-90,162.7-128,189.7c0,0-14,10-26,16 c-16,8-24,11-24,11s-63,27-131,91s-122,185-186,244s-163,152.7-206,263.3c-9,23.2-18.2,43.9-27.5,62.3c56.5,0,1255.5-0.1,1255.5-0.1 L1551.5-1044.5z"
            />
          </g>

          <g clipPath="url(#orbClip1)">
            <image
              className="scratches"
              width="150"
              href="https://assets.codepen.io/721952/scratches2.png"
            />
            <use href="#wave2" fill="#FD3B84" />
            <use href="#wave1" fill="#8D1B1B" />
          </g>

          <path
            id="star"
            fill="#FFF59D"
            d="M397,17.6c3-3,6.1-9.1,6.1-9.1s0.8,3.8,4.5,7.6c6.1,6.1,9.1,6.1,9.1,6.1s-3,1.5-7.6,6.1 s-6.1,9.1-6.1,9.1s-2.3-5.3-6.1-9.1s-8.3-5.3-8.3-5.3S394,20.6,397,17.6z"
          />

          <g id="logo">
            <image
              href={process.env.PUBLIC_URL + "/images/Love_All.avif"}
              x="490"
              y="42"
              width="55"
              height="55"
            />
          </g>

          <g fill="#FFF59D" strokeLinecap="round" style={{ pointerEvents: "none" }}>
            <text x="45" y="60" fontSize="26">
              Social Loyalty Card
            </text>
            <text
              x="300"
              y="200"
              fontSize="42"
              letterSpacing="2"
              textAnchor="middle"
            >
              {formatCardNumber(card_number)}
            </text>
            <text x="45" y="320" fontSize="18">
              {displayName}
            </text>
            <text x="355" y="295" fontSize="12">
              EXPIRY:
            </text>
            <text x="355" y="320" fontSize="18">
              {displayExpiry}
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default LoyaltyCard;
